/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { FC, memo, useState } from 'react';
import {
  XIcon,
  Camera,
  Bell,
  Settings as SettingsIcon,
  Eye,
  EyeOff,
} from 'lucide-react';

import { useAuth } from '../../stores/AuthContext';
import { usersService } from '../../services/usersService';
import { authService } from '../../services/authService';
import type { User as AuthUser } from '../../services/authService';
import type { User as UsersUser } from '../../services/usersService';

interface SettingsModalProps {
  modalId: string;
}

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

interface PasswordInputProps {
  id: string;
  name: string;
  label: string;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
}

const inputClassName =
  'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2C5F7C]';

const passwordInputClassName = `${inputClassName} pr-10`;

const TabButton: FC<TabButtonProps> = ({ active, onClick, children }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
        active
          ? 'bg-white text-[#0F172A] shadow-sm'
          : 'text-gray-600 hover:text-gray-800'
      }`}
    >
      {children}
    </button>
  );
};

const PasswordInput: FC<PasswordInputProps> = ({
  id,
  name,
  label,
  value,
  placeholder,
  onChange,
}) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const toggleLabel = isPasswordVisible ? 'Hide password' : 'Show password';

  return (
    <div>
      <label
        htmlFor={id}
        className="block text-sm font-medium text-gray-700 mb-1"
      >
        {label}
      </label>

      <div className="relative">
        <input
          id={id}
          name={name}
          type={isPasswordVisible ? 'text' : 'password'}
          autoComplete="new-password"
          placeholder={placeholder}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className={passwordInputClassName}
        />

        <button
          type="button"
          aria-label={toggleLabel}
          title={toggleLabel}
          onClick={() => setIsPasswordVisible((current) => !current)}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-500 hover:text-[#2C5F7C] focus:outline-none focus:ring-2 focus:ring-[#2C5F7C]"
        >
          {isPasswordVisible ? (
            <Eye className="h-4 w-4" />
          ) : (
            <EyeOff className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
  );
};

const mapAuthUserToUsersUser = (
  authUser: AuthUser,
  fallback?: UsersUser | null
): UsersUser => {
  return {
    id: authUser.id,
    email: authUser.email,
    googleId: authUser.googleId ?? '',
    auth0Id: authUser.auth0Id ?? '',
    companyId: authUser.companyId,
    firstName: authUser.firstName,
    lastName: authUser.lastName,
    lastLoggedIn: authUser.lastLoggedIn ?? '',
    expiresAt: authUser.expiresAt ? String(authUser.expiresAt) : '',
    isActive: authUser.isActive,
    subscriptionPlan: authUser.subscriptionPlan,
    isAdmin: (authUser as any).isAdmin ?? fallback?.isAdmin ?? false,
    companyName: authUser.companyName,
  } as UsersUser;
};

const refreshAuthIfPossible = async (
  accessToken: string | null,
  login: (user: UsersUser, token: string) => void,
  currentUser: UsersUser | null
) => {
  if (!accessToken) return;

  try {
    const refreshed = await authService.refresh(accessToken);
    const mapped = mapAuthUserToUsersUser(
      refreshed.user,
      currentUser ?? undefined
    );

    login(mapped, refreshed.accessToken);
  } catch {
    // Ignore refresh errors to avoid disrupting UX
  }
};

const SettingsModal: FC<SettingsModalProps> = ({ modalId }) => {
  const { user, token, login } = useAuth();

  const [tab, setTab] = useState<'profile' | 'security' | 'notifications'>(
    'profile'
  );

  const email = user?.email || '';

  const [firstName, setFirstName] = useState<string>(user?.firstName || '');
  const [lastName, setLastName] = useState<string>(user?.lastName || '');
  const [emailNotif, setEmailNotif] = useState<boolean>(true);

  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');

  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [profileMessage, setProfileMessage] = useState<string>('');
  const [profileError, setProfileError] = useState<string>('');

  const getPrimaryLabel = () => {
    if (tab === 'profile') return 'Save Changes';
    if (tab === 'security') return 'Update Password';

    return 'Save Preferences';
  };

  const primaryLabel = getPrimaryLabel();

  const handleUpdatePassword = async () => {
    if (!user?.id) {
      setPasswordError('User not found');
      return;
    }

    if (!newPassword || !confirmPassword) {
      setPasswordError('Please fill in all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    setIsUpdatingPassword(true);
    setPasswordMessage('');
    setPasswordError('');

    try {
      await usersService.updatePassword(user.id, newPassword);
      setPasswordMessage('Password updated successfully');
      setNewPassword('');
      setConfirmPassword('');

      await refreshAuthIfPossible(token, login, user as any);
    } catch (error) {
      setPasswordError(
        error instanceof Error ? error.message : 'Failed to update password'
      );
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleUpdateProfile = async () => {
    if (!user?.id || !user?.email || !user?.companyId) {
      setProfileError('User information not found');
      return;
    }

    if (!firstName || !lastName) {
      setProfileError('Please fill in both first and last name');
      return;
    }

    setIsUpdatingProfile(true);
    setProfileMessage('');
    setProfileError('');

    try {
      await usersService.updateUser(user.id, {
        firstName,
        lastName,
        email: user.email,
        companyId: Number(user.companyId),
        isAdmin: user.isAdmin,
      });

      setProfileMessage('Profile updated successfully');

      await refreshAuthIfPossible(token, login, user as any);
    } catch (error) {
      setProfileError(
        error instanceof Error ? error.message : 'Failed to update profile'
      );
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const getFooterButtonHandler = () => {
    if (tab === 'profile') return handleUpdateProfile;
    if (tab === 'security') return handleUpdatePassword;

    return undefined;
  };

  const getFooterButtonLabel = () => {
    if (tab === 'profile' && isUpdatingProfile) return 'Updating...';
    if (tab === 'security' && isUpdatingPassword) return 'Updating...';

    return primaryLabel;
  };

  return (
    <dialog id={modalId} className="modal" aria-labelledby={`${modalId}-title`}>
      <article className="modal-box max-w-3xl bg-white p-0 rounded-2xl overflow-hidden h-[640px] flex flex-col">
        <header className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2 text-[#0F172A]">
            <SettingsIcon className="h-5 w-5" />
            <h2 id={`${modalId}-title`} className="text-lg font-semibold">
              Settings
            </h2>
          </div>

          <form method="dialog" className="m-0">
            <button
              type="submit"
              className="btn btn-ghost text-gray-400 hover:text-gray-600"
              aria-label="Close settings"
            >
              <XIcon size={20} />
            </button>
          </form>
        </header>

        <div className="px-6 pt-4">
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1 w-fit">
            <TabButton
              active={tab === 'profile'}
              onClick={() => setTab('profile')}
            >
              Profile
            </TabButton>

            <TabButton
              active={tab === 'security'}
              onClick={() => setTab('security')}
            >
              Security
            </TabButton>

            <TabButton
              active={tab === 'notifications'}
              onClick={() => setTab('notifications')}
            >
              Notifications
            </TabButton>
          </div>
        </div>

        <section className="px-6 py-6 flex-1 overflow-y-auto">
          {tab === 'profile' && (
            <div className="space-y-6">
              <div className="rounded-xl bg-[#EEF5FB] p-6 flex items-center gap-6">
                <div className="relative">
                  <div className="w-20 h-20 rounded-full bg-[#0F172A] text-white flex items-center justify-center text-2xl font-semibold">
                    T
                  </div>

                  <button
                    type="button"
                    className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-white border border-gray-200 shadow flex items-center justify-center"
                    aria-label="Change avatar"
                  >
                    <Camera className="h-4 w-4 text-gray-700" />
                  </button>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-[#0F172A]">
                    Update your profile
                  </h3>
                  <p className="text-gray-600 text-sm">{email}</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="settings-email"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Email Address
                  </label>

                  <input
                    id="settings-email"
                    name="email"
                    type="email"
                    value={email}
                    disabled
                    className={inputClassName}
                  />

                  <p className="text-xs text-gray-500 mt-1">
                    * Your email cannot be changed.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="settings-first-name"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      First Name
                    </label>

                    <input
                      id="settings-first-name"
                      name="firstName"
                      type="text"
                      placeholder="Enter your first name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className={inputClassName}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="settings-last-name"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Last Name
                    </label>

                    <input
                      id="settings-last-name"
                      name="lastName"
                      type="text"
                      placeholder="Enter your last name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className={inputClassName}
                    />
                  </div>
                </div>
              </div>

              {profileMessage && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                  <p className="text-sm text-green-700">{profileMessage}</p>
                </div>
              )}

              {profileError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-700">{profileError}</p>
                </div>
              )}
            </div>
          )}

          {tab === 'security' && (
            <div className="space-y-4">
              <div className="rounded-lg border border-gray-200 bg-white p-6">
                <h3 className="text-base font-semibold text-[#0F172A] mb-4">
                  Update Password
                </h3>

                <div className="space-y-4">
                  <PasswordInput
                    id="new-password"
                    name="new-password"
                    label="New Password"
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={setNewPassword}
                  />

                  <p className="text-xs text-gray-500 mt-1">
                    Must be at least 8 characters
                  </p>

                  <PasswordInput
                    id="confirm-password"
                    name="confirm-password"
                    label="Confirm Password"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={setConfirmPassword}
                  />

                  {passwordMessage && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                      <p className="text-sm text-green-700">
                        {passwordMessage}
                      </p>
                    </div>
                  )}

                  {passwordError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-700">{passwordError}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {tab === 'notifications' && (
            <div className="space-y-6">
              <div className="flex items-center gap-2 text-[#0F172A]">
                <Bell className="h-5 w-5" />
                <h3 className="text-lg font-semibold">
                  Notification Preferences
                </h3>
              </div>

              <div className="flex items-center justify-between border rounded-lg p-4">
                <div>
                  <p
                    id="email-notifications-label"
                    className="text-sm font-medium text-gray-900"
                  >
                    Email Notifications
                  </p>

                  <p className="text-sm text-gray-600">
                    Receive updates about your sessions and reports
                  </p>
                </div>

                <label
                  htmlFor="email-notifications-toggle"
                  className="inline-flex items-center cursor-pointer relative"
                >
                  <span className="sr-only">Toggle email notifications</span>

                  <input
                    id="email-notifications-toggle"
                    type="checkbox"
                    aria-labelledby="email-notifications-label"
                    className="sr-only peer"
                    checked={emailNotif}
                    onChange={(e) => setEmailNotif(e.target.checked)}
                  />

                  <span className="w-11 h-6 bg-gray-200 rounded-full peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#2C5F7C] relative peer-checked:bg-[#0F172A] transition-colors">
                    <span className="absolute top-0.5 left-[2px] h-5 w-5 rounded-full bg-white border border-gray-300 transition-all peer-checked:translate-x-full peer-checked:border-white" />
                  </span>
                </label>
              </div>
            </div>
          )}
        </section>

        <footer className="mt-auto flex items-center justify-end gap-4 px-6 py-4 border-t border-gray-200">
          <button
            type="button"
            onClick={getFooterButtonHandler()}
            disabled={
              (tab === 'profile' &&
                (isUpdatingProfile || !firstName || !lastName)) ||
              (tab === 'security' &&
                (isUpdatingPassword || !newPassword || !confirmPassword))
            }
            className="btn btn-secondary px-4 py-2 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {getFooterButtonLabel()}
          </button>
        </footer>
      </article>
    </dialog>
  );
};

export default memo(SettingsModal);
